import React from 'react';

import { getRouteApi } from '@tanstack/react-router';
import { Badge } from 'cpool-components/general/Badge';
import { COLORS } from 'cpool-components/types';
import { AGGREGATE_NAMES } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { LuX } from 'react-icons/lu';

import type { MonthKey } from './dateCategoryUtils';
import { buildDocuments, selectedMonthsFromDocuments } from './dateCategoryUtils';
import { MonthRangePicker } from './MonthRangePicker';
import de_locale from '~/features/leadDetails/components/Overview/CustomerData/General/utils/de_locale.json';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import type {
    SchemaDemandedDocumentEntry,
    SchemaDemandedDocumentsKeys,
} from '~/features/leadDetails/schemas/demandedDocuments.schema';

type Props = {
    title: string;
    documents: SchemaDemandedDocumentEntry[];
    category: SchemaDemandedDocumentsKeys;
    remove: (path: SchemaDemandedDocumentsKeys, name: string) => void;
    set: (path: SchemaDemandedDocumentsKeys, documents: SchemaDemandedDocumentEntry[]) => void;
};

// Document names stay German for backend consistency; only the calendar overview is localised.
const GERMAN_MONTH_NAMES = de_locale.months.map(([full]) => full);

const DateCategory: React.FC<Props> = ({ category, documents, remove, set, title }) => {
    const { t } = useTranslation();
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const disabled = !permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].demandDocuments;

    const categoryLabel = t(`DOCUMENT_DEMANDER.CATEGORIES_KEYS.${category}`);
    const selectedMonths = selectedMonthsFromDocuments(documents, GERMAN_MONTH_NAMES);

    const onSelectionChange = (months: MonthKey[]) =>
        set(category, buildDocuments(months, categoryLabel, GERMAN_MONTH_NAMES));

    return (
        <div className="flex flex-col gap-2 rounded-md border border-grey-stroke bg-surface-light px-4 py-3">
            <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-midnight">{title}</p>
                <MonthRangePicker disabled={disabled} selectedMonths={selectedMonths} onChange={onSelectionChange} />
            </div>
            {documents.length > 0 && (
                <div className="flex flex-wrap items-center gap-[3px]">
                    {documents.map(({ name }) =>
                        disabled ? (
                            <Badge key={name} color={COLORS.WHITE} className="font-medium">
                                {name}
                            </Badge>
                        ) : (
                            <Badge
                                key={name}
                                color={COLORS.WHITE}
                                className="font-medium"
                                aria-label={t('DOCUMENT_DEMANDER.PREVIEW.REMOVE_DOCUMENT', { name })}
                                iconRight={<LuX />}
                                onClick={() => remove(category, name)}
                            >
                                {name}
                            </Badge>
                        ),
                    )}
                </div>
            )}
        </div>
    );
};

export default DateCategory;
